import { defineStore } from 'pinia'
import axios from 'axios'
import { authStore } from '@/stores/auth'

// The provider's customer shops, and the customer-facing email for an inspection. A provider uploads
// estimates on a shop's behalf, so the inspection is created under the provider's own ADAS Map firm and
// carries no trace of the shop; this is the CCP-side record of which shop it came from.
const base = () => (window.config ? window.config.apiUrl : '')
const auth = () => ({ headers: { Authorization: `Bearer ${authStore().token}` } })

export const customerStore = defineStore('customer', {
  state: () => ({
    shops: [],
    inspectionShop: null,
    loadingShops: false,
    sending: false
  }),
  actions: {
    // firm names a location beneath the caller (the locations tree edits one location at a time);
    // omitted means the caller's own firm.
    async getShops(searchterm, firm) {
      this.loadingShops = true
      try {
        const parts = []
        if (searchterm) parts.push(`searchterm=${encodeURIComponent(searchterm)}`)
        if (firm) parts.push(`firm=${encodeURIComponent(firm)}`)
        const q = parts.length ? `?${parts.join('&')}` : ''
        const resp = await axios.get(`${base()}/customer/shops${q}`, auth())
        this.shops = resp.data || []
        return this.shops
      } finally {
        this.loadingShops = false
      }
    },
    async saveShop(shop) {
      const resp = await axios.post(`${base()}/customer/shop`, shop, auth())
      return resp.data || {}
    },
    // Returns {} when the inspection did not come from a provider upload.
    async getInspectionShop(source, adasInspection) {
      const resp = await axios.get(
        `${base()}/customer/inspectionShop?source=${encodeURIComponent(source)}&adasInspection=${adasInspection}`,
        auth()
      )
      this.inspectionShop = resp.data && resp.data.customerShop ? resp.data : null
      return this.inspectionShop
    },
    async saveInspectionShop(link) {
      const resp = await axios.post(`${base()}/customer/inspectionShop`, link, auth())
      return resp.data || {}
    },
    async getEmailTemplate() {
      const resp = await axios.get(`${base()}/customer/emailTemplate`, auth())
      return resp.data || {}
    },
    async saveEmailTemplate(template) {
      const resp = await axios.post(`${base()}/customer/emailTemplate`, template, auth())
      return resp.data || {}
    },
    // The sign-up callout is configured once on the CCP root firm (1). FirmPreference_Get walks up the
    // hierarchy, so every provider inherits it without holding its own copy.
    async getSignupCallout() {
      const resp = await axios.get(
        `${base()}/firm/preferences?firm=1&category=AdasMap&name=ProviderCustomerCallout`,
        auth()
      )
      const data = resp.data
      const row = Array.isArray(data) ? data[0] : data
      return (row && row.value) || ''
    },
    async saveSignupCallout(html) {
      const resp = await axios.post(
        `${base()}/firm/savePreference`,
        {
          firm: 1,
          firmPreferenceCategory: 'AdasMap',
          firmPreferenceName: 'ProviderCustomerCallout',
          preference: html,
          visualName: 'ADAS Map sign-up callout'
        },
        auth()
      )
      return resp.data || {}
    },
    async previewCustomerEmail(source, adasInspection) {
      const resp = await axios.get(
        `${base()}/customer/emailCustomer/preview?source=${encodeURIComponent(source)}&adasInspection=${adasInspection}`,
        auth()
      )
      return resp.data || {}
    },
    async emailCustomer(request) {
      this.sending = true
      try {
        const resp = await axios.post(`${base()}/customer/emailCustomer`, request, auth())
        return resp.data || {}
      } finally {
        this.sending = false
      }
    }
  }
})
